import { useState, type ComponentType } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CircleUser, RefreshCcw } from 'lucide-react'
import { useAuth } from '@/src/renderer/contexts/AuthProvider'
import { useSync } from '@/src/renderer/contexts/SyncProvider'
import { HappyTaskLoadingLogo } from '@/src/renderer/components/icons/HappyTaskLoadingLogo'
import { CloudIcon } from '@/src/renderer/components/icons/CloudIcon'
import LanguageSelector from '@/src/renderer/components/common/LanguageSelector/LanguageSelector'
import { AccountProfileSection } from '@/src/renderer/components/common/preferences/AccountProfileSection'
import { SettingsSection, SettingsRow } from '@/src/renderer/components/common/preferences/primitives'
import { Button } from '@/src/renderer/components/ui/Button'
import { SyncPlansSection } from './SyncPlansSection'
import './AccountPage.scss'

type Section = 'profile' | 'sync'

const NAV_ITEMS: { id: Section; icon: ComponentType<{ size?: number; strokeWidth?: number }>; labelKey: string }[] = [
  { id: 'profile', icon: CircleUser, labelKey: 'account.nav.profile' },
  { id: 'sync', icon: RefreshCcw, labelKey: 'account.nav.sync' },
]

function Loader() {
  return (
    <div className="account-page__loader">
      <HappyTaskLoadingLogo />
    </div>
  )
}

/**
 * Web-only Account page (`/account`) — the hard-paywall gate for a Cloud user
 * without a Sync entitlement. Modeled on the Preferences dialog: a left nav
 * (Profil → Sync, with the Settings icons + teal glow) + content, in its own
 * shell, centered at max 1120px. Language + logout live at the bottom of the nav.
 *
 * Gate-only: an entitled user is redirected into the app; an unauthenticated
 * user back to login. Activating sync (free in the beta) flips the entitlement,
 * which redirects into the app via the guard below.
 *
 * Email change + account deletion are DISABLED placeholders — out of scope here.
 */
export function AccountPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isAuthenticated, loading, logout } = useAuth()
  const { syncEntitlement, entitlementLoaded } = useSync()
  const [section, setSection] = useState<Section>('profile')

  if (loading) return <Loader />
  if (!isAuthenticated) return <Navigate to="/" replace />
  if (!entitlementLoaded) return <Loader />
  if (syncEntitlement) return <Navigate to="/home" replace />

  const handleLogout = async () => {
    const result = await logout()
    if (result.success) navigate('/', { replace: true })
  }

  return (
    <div className="account-page">
      <div className="account-page__inner">
        <header className="account-page__header">
          <div className="account-page__brand">
            <CloudIcon width={40} className="account-page__brand-icon" />
            <span className="account-page__brand-name">HappyCloud</span>
          </div>
        </header>

        <div className="account-page__body">
          {/* data-section drives the teal/green glow tint, mirroring the
              account-data section of the Settings side nav. */}
          <nav className="account-page__nav" data-section="account-data" aria-label="HappyCloud">
            <ul className="account-page__nav-list">
              {NAV_ITEMS.map(({ id, icon: Icon, labelKey }) => {
                const active = section === id
                return (
                  <li key={id}>
                    <button
                      type="button"
                      className={`account-page__nav-item${active ? ' account-page__nav-item--active' : ''}`}
                      aria-current={active}
                      onClick={() => setSection(id)}
                    >
                      {active && <span aria-hidden="true" className="account-page__nav-glow" />}
                      <span className="account-page__nav-icon">
                        <Icon size={16} strokeWidth={1.75} />
                      </span>
                      <span className="account-page__nav-label">{t(labelKey)}</span>
                    </button>
                  </li>
                )
              })}
            </ul>

            <div className="account-page__nav-footer">
              <LanguageSelector fullWidth />
              <Button variant="secondary" onClick={handleLogout}>
                {t('account.logout')}
              </Button>
            </div>
          </nav>

          <main className="account-page__content">
            {section === 'profile' ? (
              <>
                <AccountProfileSection />
                {/* Account-level actions — placeholders, not yet functional. */}
                <SettingsSection title={t('account.title')}>
                  <SettingsRow
                    label={t('account.profile.emailChange')}
                    description={t('account.profile.comingSoon')}
                    control={
                      <Button variant="secondary" disabled>
                        {t('account.profile.emailChange')}
                      </Button>
                    }
                  />
                  <SettingsRow
                    label={t('account.profile.deleteAccount')}
                    description={t('account.profile.comingSoon')}
                    control={
                      <Button variant="secondary" disabled>
                        {t('account.profile.deleteAccount')}
                      </Button>
                    }
                  />
                </SettingsSection>
              </>
            ) : (
              <SyncPlansSection />
            )}
          </main>
        </div>
      </div>
    </div>
  )
}
